<template>
  <div>
    <p class="section-export-label">Export</p>
    <template v-if="exportReady">
      <p class="section-export-copy">
        Your section is ready. The download link expires after 15 minutes.
      </p>
      <a
        class="section-export-button section-export-download"
        :href="exportState.download_url"
        download
        rel="noopener"
        @click="$emit('download-export', { eventId: activeEvent.id })"
      >
        Download MP4
      </a>
    </template>
    <template v-else-if="exportPending">
      <p class="section-export-copy" role="status">
        Preparing your MP4 in the background…
      </p>
      <span class="section-export-timing">
        {{ formatDuration(sectionStartSeconds) }}–{{ formatDuration(sectionEndSeconds) }}
      </span>
    </template>
    <template v-else-if="confirmationOpen">
      <p class="section-export-copy">
        Prepare {{ formatDuration(sectionDurationSeconds) }} from
        {{ formatDuration(sectionStartSeconds) }}–{{ formatDuration(sectionEndSeconds) }}?
      </p>
      <p class="section-export-note">
        The MP4 is available for 7 days. Preparing it may take a few minutes.
      </p>
      <div class="section-export-actions">
        <button type="button" class="section-export-button" @click="exportEvent">
          Prepare MP4
        </button>
        <button
          type="button"
          class="section-export-button section-export-button-secondary"
          @click="confirmationOpen = false"
        >
          Cancel
        </button>
      </div>
    </template>
    <template v-else>
      <p v-if="exportError" class="section-export-error" role="alert">
        {{ exportError }}
      </p>
      <p v-if="!canExportSection" class="section-export-error">
        Sections must be between 1 second and
        {{ formatDuration(MAX_SECTION_EXPORT_SECONDS) }}.
      </p>
      <p v-else class="section-export-copy">Prepare this exact section as an MP4.</p>
      <button
        type="button"
        class="section-export-button"
        :disabled="!canExportSection"
        @click="confirmationOpen = true"
      >
        {{ exportState.export_id ? 'Check export status' : 'Export section' }}
      </button>
    </template>
  </div>
</template>

<script>
import {
  MAX_SECTION_EXPORT_SECONDS,
  isSectionExportPending,
} from '../../../api/sectionExports.js'

export default {
  name: 'CeremonySectionExport',
  props: {
    activeEvent: {
      type: Object,
      required: true,
    },
    exportState: {
      type: Object,
      default: () => ({}),
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      confirmationOpen: false,
      MAX_SECTION_EXPORT_SECONDS,
    }
  },
  computed: {
    sectionStartSeconds() {
      return Number(
        this.activeEvent.cluster_start_sec ??
        this.activeEvent.windowStartSeconds ??
        this.activeEvent.timestamp
      )
    },
    sectionEndSeconds() {
      return Number(
        this.activeEvent.cluster_end_sec ??
        this.activeEvent.windowEndSeconds
      )
    },
    sectionDurationSeconds() {
      return this.sectionEndSeconds - this.sectionStartSeconds
    },
    canExportSection() {
      return (
        Number.isFinite(this.sectionStartSeconds) &&
        Number.isFinite(this.sectionEndSeconds) &&
        this.sectionStartSeconds >= 0 &&
        this.sectionDurationSeconds >= 1 &&
        this.sectionDurationSeconds <= MAX_SECTION_EXPORT_SECONDS
      )
    },
    exportStatus() {
      return String(this.exportState.status || '').toUpperCase()
    },
    exportPending() {
      return isSectionExportPending(this.exportState)
    },
    exportReady() {
      return this.exportStatus === 'COMPLETE' && Boolean(this.exportState.download_url)
    },
    exportError() {
      if (!this.exportState.error && !['ERROR', 'CANCELED'].includes(this.exportStatus)) {
        return ''
      }
      return this.exportState.error || 'The section export failed. You can try again.'
    },
  },
  watch: {
    'activeEvent.id'() {
      this.confirmationOpen = false
    },
  },
  methods: {
    exportEvent() {
      if (!this.canExportSection || this.exportPending) return
      this.confirmationOpen = false
      this.$emit('export-event', {
        eventId: this.activeEvent.id,
        event: this.activeEvent,
      })
    },
  },
}
</script>

<style scoped>
.section-export-label {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-export-copy,
.section-export-note,
.section-export-error {
  margin: 0 0 10px;
  font-size: 0.86rem;
  line-height: 1.45;
}

.section-export-copy {
  color: #334155;
}

.section-export-note,
.section-export-timing {
  color: #64748b;
}

.section-export-error {
  color: #b42318;
}

.section-export-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.section-export-button {
  min-width: 86px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: .25rem;
  background: #0f172a;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
}

.section-export-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.section-export-button-secondary {
  background: #fff;
  color: #0f172a;
}

.section-export-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.section-export-timing {
  display: inline-block;
  font-size: 0.8rem;
}
</style>
